import { type IReactionDisposer, makeAutoObservable, reaction, runInAction } from 'mobx'
import { isAxiosError } from 'axios'
import { TableStore } from 'shared/ui/Table'
import { logger } from 'shared/lib'
import { RequestLoadData } from 'shared/api/lib'
import {
  Campaign,
  CampaignApi,
  type IParamsGetList,
  type IResponseGetList,
  type IResponseShortCampaign,
} from 'entities/Campaign'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { tableLimitStore } from 'entities/TableLimit'
import { FiltersAndSearchStore } from 'widgets/FiltersAndSearch'

export class CampaignsListStore {
  filtersAndSearchStore
  tableStore
  _disposeParamsGetItems: IReactionDisposer | null = null
  _disposeParamsWithoutPage: IReactionDisposer | null = null

  startPaginationSize = 10
  limit = tableLimitStore.getLimit('campaigns-table') ?? 10
  private _hasRetriedAtMinLimit = false
  page = 1
  total = 0

  itemsMap = new Map<number, Campaign>()
  firstLoading = true

  constructor(private _entities: IEntitiesStore) {
    makeAutoObservable(this)

    this.filtersAndSearchStore = new FiltersAndSearchStore(
      {
        getFilters: () => CampaignApi.getFilters().then(({ data }) => data.data),
      },
      this._entities
    )
    this.tableStore = new TableStore<Campaign>({
      saveColumns: this._saveColumns,
      getColumns: this._getColumns(),
    })
    this._requestLoadData.setLoading(true)
  }

  dispose = () => {
    this._disposeParamsGetItems?.()
    this._disposeParamsWithoutPage?.()
    this._requestLoadData.dispose()
  }

  get paramsWithoutPage(): Omit<IParamsGetList, 'page'> {
    return {
      limit: this.limit,
      search: this.filtersAndSearchStore.search,
      filters: this.filtersAndSearchStore.noEmptyFilters,
    }
  }

  get paramsGetItems(): IParamsGetList {
    return {
      ...this.paramsWithoutPage,
      page: this.page,
    }
  }

  get items() {
    return Array.from(this.itemsMap.values())
  }

  setItem = (response: IResponseShortCampaign) => {
    const campaign: Campaign = new Campaign(
      response,
      () => this.onSuccessDelete(response.id),
      this._entities
    )
    this.itemsMap.set(campaign.id, campaign)
  }

  setItems = (items: Array<IResponseShortCampaign>) => {
    items.forEach((item) => {
      this.setItem(item)
    })
  }

  setLoading = (value: boolean) => {
    this._requestLoadData.setLoading(value)
    this.firstLoading = false
  }

  init = () => {
    this._disposeParamsGetItems = reaction(
      () => this.paramsGetItems,
      () => this._requestLoadData.debounceLoadData()
    )
    this._disposeParamsWithoutPage = reaction(
      () => this.paramsWithoutPage,
      () => {
        this._requestLoadData.setLoading(true)
        this.page = 1
      }
    )

    this.loadData()

    return this.dispose
  }

  onPaginationModelChange = (page: number, limit: number) => {
    this.page = page
    this.limit = limit
    tableLimitStore.setLimit('campaigns-table', limit)
  }

  get isEmpty() {
    return !this.total && !this.filtersAndSearchStore.hasSearchParams && !this.loading
  }

  private _requestLoadData = new RequestLoadData<boolean>({
    requestCallback: async ({ cancelRequest }, noLoading) => {
      runInAction(() => {
        if (noLoading) {
          this._requestLoadData.setLoading(false)
        }
      })

      try {
        const responseData = await CampaignApi.getList(this.paramsGetItems, {
          cancelToken: cancelRequest.token,
        })
        const data = responseData.data
        runInAction(() => {
          this._hasRetriedAtMinLimit = false
        })
        this._setData(data)
      } catch (error) {
        const isTimeout =
          isAxiosError(error) && (error.response?.status === 408 || error.response?.status === 504)
        if (isTimeout) {
          logger.warn(
            'TABLE: campaigns-table Request timed out, likely due to large data volume. Consider adding more filters to reduce the dataset size.'
          )
          const currentLimit = this.limit
          const newLimit = tableLimitStore.reduceLimit('campaigns-table', currentLimit)
          if (newLimit !== currentLimit) {
            runInAction(() => {
              this._hasRetriedAtMinLimit = false
            })
            this.limit = newLimit
            tableLimitStore.setLimit('campaigns-table', newLimit)
          } else if (!this._hasRetriedAtMinLimit) {
            runInAction(() => {
              this._hasRetriedAtMinLimit = true
            })
            this._requestLoadData.debounceLoadData()
          }
        } else {
          throw error
        }
      }
    },
    finallyCallback: () => {
      this.setLoading(false)
    },
  })

  private loadData = async (noLoading?: boolean) => {
    return this._requestLoadData.loadData(noLoading)
  }

  private _setData = ({ data, meta }: IResponseGetList) => {
    this.itemsMap.clear()
    this.setItems(data)
    this.total = meta.total
  }

  onSuccessDelete = (id: number) => {
    this.itemsMap.delete(id)
    if (this.itemsMap.size === 0 && this.page > 1) {
      this.page -= 1
    }
  }

  private _saveColumns = async (items: string[]) => {
    try {
      await CampaignApi.saveColumns({
        source_type: 'campaigns-table-columns',
        items,
      })
    } catch (e) {
      logger.error(e)
    }
  }
  private _getColumns = async () => {
    try {
      const { data } = await CampaignApi.getColumns()

      if (!data.data) return

      return data.data.items
    } catch (e) {
      logger.error(e)
    }
  }

  get loading() {
    return this._requestLoadData.loading
  }
}
